import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DOMPurify from "dompurify";
import {
  LuMapPin,
  LuClock,
  LuBuilding2,
  LuDollarSign,
  LuGraduationCap,
  LuSparkles,
  LuExternalLink,
  LuInfo,
  LuRefreshCw,
  LuGift,
  LuListChecks,
  LuBookOpen,
  LuStar,
} from "react-icons/lu";
import PublicNav, { PublicFooter } from "../components/PublicNav";
import SEO from "../components/SEO";
import { Btn, Card, Badge, Skel, Note } from "../components/Ui";
import { setdetailedJob } from "../../store/actions/actions";
import { FormattedDescription } from "./JobSearch";
import "./jobs.css";

const JOB_DETAIL_API = "https://us-east1-foursssolutions.cloudfunctions.net/Get_Jobs_with_job_id_v2";

function getDaysAgo(postedDate) {
  const posted = new Date(postedDate);
  const today = new Date();
  posted.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  const differenceInDays = Math.floor((today - posted) / (1000 * 60 * 60 * 24));
  if (differenceInDays <= 0) return "Today";
  return `${differenceInDays} day${differenceInDays > 1 ? "s" : ""} ago`;
}

function SectionCard({ icon, title, children }) {
  return (
    <Card pad>
      <div className="chunk-title mb-2">
        {icon}
        <span className="t-h3">{title}</span>
      </div>
      {children}
    </Card>
  );
}

function BulletList({ items }) {
  return (
    <ul className="sug-list">
      {items.map((item, index) => (
        <li key={index}>
          <LuStar size={13} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export default function PublicJobDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { apiToken } = useSelector((s) => s.profile);

  const [jobId, setJobId] = useState("");
  const [detailedData, setDetailedData] = useState(null);
  const [similarData, setSimilarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* legacy public jobDetailed showSecondary behavior — a fixed action
     bar appears once the page scrolls past 20px (passive listener,
     initial call on mount, removed on unmount) */
  const [showSecondary, setShowSecondary] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      setShowSecondary(y > 20);
    };
    onScroll(); // set initial state on mount
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* extract jobId from ?id= — legacy restores '+' that URL decoding
     turned into spaces */
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const rawToken = queryParams.get("id");
    const tokenFromUrl = rawToken?.replace(/ /g, "+");
    if (tokenFromUrl) setJobId(tokenFromUrl);
  }, [location.search]);

  const fetchJobDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(JOB_DETAIL_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({ job_id: jobId }),
      });
      const data = await response.json();
      if (response.ok) {
        setDetailedData(data.job_details);
        setSimilarData(data.similar_jobs);
      } else {
        console.error("Failed to fetch job details:", data);
        setError("We could not load this job. It may have been removed.");
      }
    } catch (err) {
      console.error("Error fetching job details:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) {
      fetchJobDetails();
      dispatch(setdetailedJob(jobId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const isClosed = detailedData?.job_status === "Closed";

  /* ------- overview normalization (legacy public-page logic,
     numbered points first) ------- */
  /* ATS + JSearch jobs store job_description as a plain STRING; only internal
     employer-posted jobs use the {overview, additional_comments} object shape */
  const jdRaw = detailedData?.job_description;
  const isPlainDescription = typeof jdRaw === "string" && jdRaw.trim().length > 0;
  const overviewRaw = isPlainDescription ? null : jdRaw?.overview;
  const overviewHtmlRaw = Array.isArray(overviewRaw) ? overviewRaw[0] || "" : overviewRaw || "";
  const sanitizedOverviewHtml = DOMPurify.sanitize(overviewHtmlRaw.replace(/\.,\s*/g, ". "));

  const additionalRaw = isPlainDescription ? null : jdRaw?.additional_comments;
  const additionalHtmlRaw = Array.isArray(additionalRaw) ? additionalRaw[0] || "" : additionalRaw || "";
  const sanitizedAdditionalHtml = DOMPurify.sanitize(additionalHtmlRaw);
  /* legacy renders Additional comments only when the raw field's own
     length (array length for arrays, string length otherwise) is > 1 */
  const showAdditionalComments = (additionalRaw?.length ?? 0) > 1;

  const cleanedItems = useMemo(() => {
    const items = Array.isArray(overviewRaw)
      ? overviewRaw
      : typeof overviewRaw === "string"
      ? overviewRaw.match(/(\d+\.\s.*?)(?=\s*\d+\.|$)/gs) ||
        (overviewRaw.includes("\n")
          ? overviewRaw.split(/\r?\n/)
          : overviewRaw.split(/(?<=[.!?])\s+(?=[A-Z])/))
      : [];
    return items.map((item) => String(item).trim()).filter(Boolean);
  }, [overviewRaw]);

  const facts = [
    {
      icon: <LuDollarSign size={17} />,
      tone: "green",
      label: "Salary range",
      value: `${detailedData?.salary_range || "Not specified"} ${
        detailedData?.salary_type === "Hourly" ? "/ Hour" : "/ Year"
      }`,
    },
    {
      icon: <LuGraduationCap size={17} />,
      tone: "blue",
      label: "Experience",
      value: `${detailedData?.experience_range || "Not specified"}`,
    },
    {
      icon: <LuBuilding2 size={17} />,
      tone: "purple",
      label: "Employment type",
      value: `${detailedData?.job_employment_type || "Not specified"}`,
    },
    {
      icon: <LuClock size={17} />,
      tone: "orange",
      label: "Posted",
      value: `${
        isClosed
          ? "Position Closed"
          : detailedData?.job_posted_at_datetime_utc
          ? getDaysAgo(detailedData.job_posted_at_datetime_utc)
          : "N/A"
      }`,
    },
  ];

  const responsibilities =
    detailedData?.job_highlights?.Responsibilities?.length > 0
      ? detailedData.job_highlights.Responsibilities
      : detailedData?.job_highlights?.responsibilities?.length > 0
      ? detailedData.job_highlights.responsibilities
      : null;

  const benefits =
    Array.isArray(detailedData?.job_highlights?.benefits) &&
    detailedData.job_highlights.benefits[0]?.trim().length > 0
      ? detailedData.job_highlights.benefits.filter((b) => b.trim().length > 0)
      : null;

  /* JobPosting structured data → eligible for Google for Jobs rich results */
  const jobJsonLd = detailedData
    ? {
        "@context": "https://schema.org",
        "@type": "JobPosting",
        title: detailedData.job_title,
        description: isPlainDescription
          ? jdRaw
          : overviewHtmlRaw || `${detailedData.job_title} at ${detailedData.employer_name}`,
        datePosted: detailedData.job_posted_at_datetime_utc || undefined,
        employmentType: detailedData.job_employment_type || undefined,
        hiringOrganization: { "@type": "Organization", name: detailedData.employer_name },
        jobLocation: {
          "@type": "Place",
          address: {
            "@type": "PostalAddress",
            addressLocality: detailedData.job_city,
            addressRegion: detailedData.job_state,
            addressCountry: detailedData.job_country,
          },
        },
        ...(detailedData.job_is_remote !== false
          ? {
              jobLocationType: "TELECOMMUTE",
              applicantLocationRequirements: {
                "@type": "Country",
                name: detailedData.job_country || "US",
              },
            }
          : {}),
        directApply: false,
      }
    : null;

  const jobLoc = detailedData
    ? [detailedData.job_city, detailedData.job_state].filter(Boolean).join(", ")
    : "";

  return (
    <div className="csx">
      {detailedData ? (
        <SEO
          title={`${detailedData.job_title} at ${detailedData.employer_name}`}
          description={`${detailedData.job_title} — ${detailedData.employer_name}${
            jobLoc ? ` in ${jobLoc}` : ""
          }. View the role and apply on CareerSavvy with AI resume tailoring.`}
          canonicalPath={`/job/detailed?id=${encodeURIComponent(jobId)}`}
          ogType="article"
          jsonLd={jobJsonLd}
          breadcrumbs={[{ label: detailedData.job_title, path: `/job/detailed?id=${encodeURIComponent(jobId)}` }]}
        />
      ) : (
        <SEO title="Job details" description="View job details and apply with CareerSavvy — AI job matching and resume tailoring." />
      )}
      <div className="csx-page">
        <PublicNav />
        <main className="grow" style={{ padding: "28px 0 56px" }}>
          <div className="wrap">
            <div className="detail-col">
              {loading ? (
                <>
                  <Card pad>
                    <div className="job-top">
                      <Skel w={56} h={56} r={14} />
                      <div className="grow stack-2">
                        <Skel w="60%" h={22} />
                        <Skel w="40%" h={14} />
                      </div>
                    </div>
                  </Card>
                  <div className="job-facts">
                    {[...Array(4)].map((_, i) => (
                      <Skel key={i} h={62} r={12} />
                    ))}
                  </div>
                  <Card pad>
                    <Skel h={14} />
                    <Skel h={14} w="90%" style={{ marginTop: 8 }} />
                    <Skel h={14} w="75%" style={{ marginTop: 8 }} />
                  </Card>
                </>
              ) : error ? (
                <Note tone="danger">
                  <LuInfo size={16} />
                  <span className="grow">{error}</span>
                  <Btn size="sm" variant="ghost" onClick={fetchJobDetails}>
                    <LuRefreshCw size={14} /> Retry
                  </Btn>
                </Note>
              ) : (
                <>
                  {/* ------- header ------- */}
                  <Card pad>
                    <div className="job-top">
                      <span className="job-logo-lg">
                        {detailedData?.employer_name?.substring(0, 2).toUpperCase()}
                      </span>
                      <div className="grow" style={{ minWidth: 0 }}>
                        <h1 className="t-h1">{detailedData?.job_title}</h1>
                        <div className="meta-row t-sm mt-1">
                          <span>{detailedData?.employer_name}</span>
                          <span className="meta-dot">
                            <LuMapPin size={12} /> {detailedData?.job_city}, {detailedData?.job_state},{" "}
                            {detailedData?.job_country}
                          </span>
                          <span className="meta-dot">
                            {detailedData?.job_is_remote === false ? "Onsite" : "Remote"}
                          </span>
                        </div>
                        {isClosed ? (
                          <div className="mt-1">
                            <Badge tone="danger">Position closed</Badge>
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className="row mt-3" style={{ flexWrap: "wrap", gap: 8 }}>
                      {isClosed ? (
                        <Btn className="btn-pill" onClick={() => navigate("/login")}>
                          Position closed — sign in
                        </Btn>
                      ) : (
                        <>
                          <Btn className="btn-pill btn-mint" onClick={() => navigate("/login")}>
                            <LuExternalLink size={15} /> Apply now
                          </Btn>
                          <Btn variant="ghost" className="btn-pill" onClick={() => navigate("/login")}>
                            <LuSparkles size={15} /> Sign in to apply with AI tailoring
                          </Btn>
                        </>
                      )}
                    </div>
                    <p className="t-xs mt-2">
                      New to CareerSavvy? <Link to="/page-register" className="t-accent">Create a free account</Link>{" "}
                      to get matched roles, resume tailoring and interview practice.
                    </p>
                  </Card>

                  {/* ------- quick facts ------- */}
                  <div className="job-facts">
                    {facts.map((f, i) => (
                      <div className="fact" key={i}>
                        <span className={`fact-ic ${f.tone}`}>{f.icon}</span>
                        <span>
                          <span className="fact-l">{f.label}</span>
                          <br />
                          <span className="fact-v">{f.value}</span>
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* ------- description sections ------- */}
                  {isPlainDescription ? (
                    <SectionCard icon={<LuBookOpen size={16} />} title="About the role">
                      <FormattedDescription text={jdRaw} />
                    </SectionCard>
                  ) : null}
                  {!isPlainDescription && cleanedItems.length === 1 ? (
                    <SectionCard icon={<LuBookOpen size={16} />} title="About the role">
                      {/* eslint-disable-next-line react/no-danger */}
                      <div
                        className="prose"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cleanedItems[0]) }}
                      />
                    </SectionCard>
                  ) : cleanedItems.length > 1 ? (
                    <SectionCard icon={<LuBookOpen size={16} />} title="Overview">
                      {/* eslint-disable-next-line react/no-danger */}
                      <div className="prose" dangerouslySetInnerHTML={{ __html: sanitizedOverviewHtml }} />
                    </SectionCard>
                  ) : null}

                  {responsibilities ? (
                    <SectionCard icon={<LuListChecks size={16} />} title="Responsibilities">
                      <BulletList items={responsibilities} />
                    </SectionCard>
                  ) : null}

                  {detailedData?.job_highlights?.requirements?.length > 0 ? (
                    <SectionCard icon={<LuStar size={16} />} title="Requirements">
                      <BulletList items={detailedData.job_highlights.requirements} />
                    </SectionCard>
                  ) : null}

                  {detailedData?.job_highlights?.Qualifications?.length > 0 &&
                  !(
                    isPlainDescription &&
                    detailedData.job_highlights.Qualifications.length === 1 &&
                    String(detailedData.job_highlights.Qualifications[0]).trim() === jdRaw.trim()
                  ) ? (
                    <SectionCard icon={<LuGraduationCap size={16} />} title="Qualifications">
                      <BulletList items={detailedData.job_highlights.Qualifications} />
                    </SectionCard>
                  ) : null}

                  {benefits ? (
                    <SectionCard icon={<LuGift size={16} />} title="Benefits">
                      <BulletList items={benefits} />
                    </SectionCard>
                  ) : null}

                  {showAdditionalComments ? (
                    <SectionCard icon={<LuInfo size={16} />} title="Additional comments">
                      {/* eslint-disable-next-line react/no-danger */}
                      <div className="prose" dangerouslySetInnerHTML={{ __html: sanitizedAdditionalHtml }} />
                    </SectionCard>
                  ) : null}

                  {/* ------- sign-in banner ------- */}
                  {!isClosed ? (
                    <Note tone="accent">
                      <LuSparkles size={16} />
                      <span className="grow">
                        Sign in to see how well your resume matches this role and generate a tailored version in one
                        click.
                      </span>
                      <Btn size="sm" className="btn-pill" to="/login">
                        Sign in
                      </Btn>
                    </Note>
                  ) : null}

                  {/* ------- related jobs ------- */}
                  {similarData?.length > 0 ? (
                    <Card pad>
                      <div className="t-h3 mb-2">Related jobs</div>
                      <div className="stack-2">
                        {similarData.map((item) => (
                          <button
                            type="button"
                            className="rel-job"
                            key={item.job_id}
                            onClick={() => navigate(`/job/detailed?id=${item.job_id}`)}
                          >
                            <span className="job-logo">{item.employer_name?.substring(0, 2).toUpperCase()}</span>
                            <span className="grow" style={{ minWidth: 0 }}>
                              <span className="t-sm t-ink" style={{ fontWeight: 600, display: "block" }}>
                                {item.job_title}
                              </span>
                              <span className="t-xs" style={{ display: "block" }}>
                                {item.employer_name} · {item?.job_city}, {item?.job_state}, {item?.job_country}
                              </span>
                              <span className="t-xs" style={{ display: "block" }}>
                                {item?.job_posted_at_datetime_utc
                                  ? getDaysAgo(item.job_posted_at_datetime_utc)
                                  : "N/A"}
                              </span>
                            </span>
                          </button>
                        ))}
                      </div>
                    </Card>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </main>

        {/* ------- sticky secondary actions (legacy showSecondary bar) ------- */}
        {showSecondary && !loading && !error && detailedData ? (
          <div className="sticky-bar" aria-label="Secondary job actions">
            <div className="sticky-id">
              <span className="job-logo">{detailedData?.employer_name?.substring(0, 2).toUpperCase()}</span>
              <div className="grow" style={{ minWidth: 0 }}>
                <div className="sticky-title trunc">{detailedData?.job_title}</div>
                <div className="sticky-sub trunc">
                  {detailedData?.employer_name} · {detailedData?.job_city}, {detailedData?.job_state},{" "}
                  {detailedData?.job_country} · {detailedData?.job_is_remote === false ? "Onsite" : "Remote"}
                </div>
              </div>
            </div>
            {isClosed ? (
              <Btn className="btn-pill" onClick={() => navigate("/login")}>
                Position closed — sign in
              </Btn>
            ) : (
              <Btn className="btn-pill btn-mint" onClick={() => navigate("/login")}>
                <LuExternalLink size={15} /> Apply now
              </Btn>
            )}
          </div>
        ) : null}

        <PublicFooter />
      </div>
    </div>
  );
}
